import type {
  CheckBoxEventValue,
  InterstateOnChangeEvent
} from "@dealer-center/core-components";
import { Box, CheckBox } from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import type {
  AccountPermissionNode,
  OperatorAuthority
} from "@dealer-center/ddc-pages-configuration-interface";
import { SCREEN_READER_ONLY } from "../StatusRegion/screenReaderOnly";

export interface AccountSubtreeProps {
  node: AccountPermissionNode;
  /** 1-based, matching `aria-level`. */
  depth: number;
  /** See `AccountNodeProps.authority`. Absent means read-only. */
  authority?: OperatorAuthority;
  // The owning root's unsaved toggles, shared by the whole subtree.
  overrides: Readonly<Record<string, boolean>>;
  onToggle: (node: AccountPermissionNode, isChecked: boolean) => void;
}

/**
 * One account in the hierarchy, and — recursively — everything beneath it. Stateless: the root
 * `AccountNode` owns the subtree's unsaved toggles and passes them down.
 *
 * **Recursive with no depth assumption** (FR-029). Live data reaches three levels
 * (`salesattributiongroup → salesattributiona → salesattributiona1`), and CC9 imposes no bound, so
 * the component recurses on `children` rather than special-casing a parent/child pair. `children` is
 * present on every live node and `[]` for leaves — the swagger omits the field entirely, so the
 * live-verified shape in `interface/` is the one to trust.
 *
 * **Nesting is conveyed programmatically, not by indentation** (FR-050): real nested lists plus an
 * explicit `aria-level`. An `sx` margin alone communicates nothing to a screen reader, and the
 * contract warns that indentation stops being legible past 4 levels anyway — so the indent is kept
 * small and the structure carries the meaning. Nested lists also keep the parent relationship
 * recoverable without horizontal scrolling.
 *
 * **Row content is name *and* identifier** (FR-028), combined into the control's accessible name so
 * one control carries both. A node whose name cannot be resolved falls back to the identifier alone —
 * never a blank row, never an error (Edge Cases). CC9 returns `""` rather than null for unset strings,
 * so that fallback is the realistic path, not a defensive one.
 *
 * Controls behave as if a save path existed: read-only without `authority`, otherwise gated by FR-042.
 * `writeAccess` supplies the initial checked state **and nothing else** (Monorepo Principle VII).
 *
 * **FR-042 is about grantability, not editability.** An account the **operator** cannot reach is not
 * grantable, and the reason is conveyed on the row. It is read from the operator's
 * `reachableAccountIds` — **not** from the subject's `writeAccess` (that is FR-054's prohibition
 * exactly), and **not** derived from `isAccountAdministrator`, which FR-058b forbids in as many
 * words: grantability "MUST NOT be evaluated against … the caller's administrator flag alone".
 */
export const AccountSubtree = ({
  node,
  depth,
  authority,
  overrides,
  onToggle
}: AccountSubtreeProps) => {
  const name = node.name?.trim();
  // FR-028's two values in one accessible name. Identifier alone when the name is unresolvable.
  const label = name ? `${name} (${node.accountId})` : node.accountId;
  /*
   * `children` is optional and the payload is unvalidated, so an absent `children` must render as a
   * leaf rather than throw. Normalised once here instead of `?.` at each of the two use sites, so the
   * recursive `.map` below cannot be reached with `undefined`.
   */
  const children = node.children ?? [];
  const hasChildren = children.length > 0;
  const isNotGrantable = Boolean(
    authority && !authority.reachableAccountIds.has(node.accountId)
  );
  const handleToggle = (event: InterstateOnChangeEvent<CheckBoxEventValue>) =>
    onToggle(node, Boolean(event.target?.value?.checked));

  return (
    <Box
      aria-level={depth}
      component={"li"}
      data-testid={`account-node-${node.accountId}`}
      /*
       * `paddingY` gives each row air. Without it the rows sit at the checkbox's own line height and
       * a hierarchy of ten accounts reads as an undifferentiated block, which is what makes the
       * indentation hard to follow — the vertical rhythm is what separates siblings, and the indent
       * only distinguishes levels once there is rhythm to indent.
       */
      sx={{ listStyle: "none", paddingY: "4px" }}
    >
      <CheckBox
        checked={overrides[node.accountId] ?? node.writeAccess}
        data-testid={`account-row-${node.accountId}`}
        disabled={!authority || isNotGrantable}
        id={`account-${node.accountId}`}
        label={label}
        name={node.accountId}
        value={node.accountId}
        onChange={handleToggle}
      />

      {/*
       * FR-042's reason, screen-reader-only. Rendered only when an `authority` exists to judge
       * against: with none the whole tab is read-only and there is no grantability question.
       */}
      {isNotGrantable && (
        <Box
          data-testid={`account-row-reason-${node.accountId}`}
          sx={SCREEN_READER_ONLY}
        >
          {Locale.getText("UM_ACCOUNT_NOT_GRANTABLE") as string}
        </Box>
      )}

      {hasChildren ? (
        /*
         * 28px per level, matching the prototype. Wide enough that a child is unambiguously indented
         * past its parent's checkbox rather than looking like a slightly-misaligned sibling — the
         * previous 20px was narrower than the checkbox-plus-gap it has to clear.
         */
        <Box
          component={"ul"}
          data-testid={`account-children-${node.accountId}`}
          sx={{ margin: 0, paddingLeft: "28px" }}
        >
          {children.map(child => (
            <AccountSubtree
              authority={authority}
              depth={depth + 1}
              key={child.accountId}
              node={child}
              overrides={overrides}
              onToggle={onToggle}
            />
          ))}
        </Box>
      ) : null}
    </Box>
  );
};
